/**
 * Tree Transform Functions for Sidewall
 * Transforms API responses to frontend tree data
 */

import type { PartsTreeRowAPI } from "../api/types";
import type { PartInfo, EquipmentStatus } from "../../../../shared/models";
import type { SidewallTreeRow } from "../frontend/types";
import {
  PART_TYPE_IDS,
  PART_IDS,
  CONTAINER_NAMES,
  SPARES_CONTAINER_NAMES,
  SPARES_CONTAINER_ORDER,
  BURNER_POSITIONS,
  SPECIAL_POSITION_IDS,
  isUpperShellChild,
  isShellPosition,
  isShellType,
  isShellSlotType,
} from "../constants/parts";
import {
  createContainerRow,
  buildSparePartsMap,
  sortAndDeduplicatePositions,
  parseExpectedChildPositions,
  type ExpectedChildPosition,
} from "../../../../shared/utils/tree-transform-helpers";

// ============================================
// Types for Internal Use
// ============================================

interface ShellInfo {
  name: string;
  status: EquipmentStatus;
  parentId: number;
  campaignValue: number;
  totalValue: number;
  initialAbsoluteValue: number;
  positionInContainer: number; // Position in the container (1=Upper, 2=Lower)
  physicalPositionId: number; // Physical position ID (18=Lower, 19=Upper) for SVG/3D mapping
  partTypeId: number; // PartTypeId (306=Upper-Shell, 311=Lower-Shell)
  lastTaskTimestamp: Date | null;
  lastTaskId: number | null;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Pattern to match burner annotations like "(#1 Burner)", "(#2 Burner)", etc.
 * Used to strip the burner suffix from L1PositionName to get the base position prefix.
 * e.g., "Sidewall 3 (#1 Burner)" → "Sidewall 3"
 */
const BURNER_ANNOTATION_PATTERN = /\s*\(#?\d+\s*Burner\)$/i;

/**
 * Strip the burner annotation from a position name.
 * @param positionName - The L1PositionName, e.g. "Sidewall 3 (#1 Burner)"
 * @param fallbackId - Position ID to use if positionName is null
 * @returns The base name, e.g. "Sidewall 3", or "Position {fallbackId}" if null
 */
const stripBurnerAnnotation = (
  positionName: string | null,
  fallbackId: number,
): string =>
  positionName?.replace(BURNER_ANNOTATION_PATTERN, "").trim() ||
  `Position ${fallbackId}`;

/**
 * Build a map of Shell IDs to shell info from API data
 * Shells (Upper Shell 600, Lower Shell 601) are direct children of containers
 */
const buildShellInfoMap = (
  apiData: PartsTreeRowAPI[],
): Map<number, ShellInfo> => {
  const shellInfoMap = new Map<number, ShellInfo>();

  // Build set of slot PartIds (permanent containers installed on Online)
  const slotPartIds = new Set(
    apiData
      .filter((r) => isShellSlotType(r.L1PartTypeId) && r.L2PartId)
      .map((r) => r.L2PartId!),
  );

  for (const row of apiData) {
    // Determine the actual part type. EQPGetPartsTree returns L1PartTypeId =
    // the POSITION's expected type, which is wrong for default-array containers
    // (Shell Spares). For example, Lower Shell sitting in Shell Spares position 1
    // is reported with L1PartTypeId=306 (Upper-Shell position) even though its
    // own type is 311. The actual installed part type lives in `IAm` (a comma-
    // separated list — for shells it's a single value).
    const iamTypeId = row.IAm ? Number(row.IAm.split(",")[0]) : null;
    const actualPartTypeId =
      iamTypeId != null && !Number.isNaN(iamTypeId)
        ? iamTypeId
        : row.L1PartTypeId;

    // A shell row has actualPartTypeId = Upper-Shell (306) or Lower-Shell (311) and L2PartId
    if (isShellType(actualPartTypeId) && row.L2PartId) {
      // Shell is online if its parent is a slot (permanent container on Online)
      const isOnline =
        slotPartIds.has(row.L1PartId ?? 0) ||
        row.L1PartId === PART_IDS.SIDEWALL;
      // Determine physical position ID and partTypeId based on shell TYPE (not PartId)
      // Upper-Shell type (306) → position 19
      // Lower-Shell type (311) → position 18
      const isUpperShell = actualPartTypeId === PART_TYPE_IDS.UPPER_SHELL;
      const physicalPositionId = isUpperShell
        ? SPECIAL_POSITION_IDS.UPPER_SHELL
        : SPECIAL_POSITION_IDS.LOWER_SHELL;
      shellInfoMap.set(row.L2PartId, {
        name: row.L2PartName || `Shell #${row.L2PartId}`,
        status: isOnline ? "Online" : "Offline",
        parentId: row.L1PartId ?? PART_IDS.SPARES,
        campaignValue: row.CampaignValue || 0,
        totalValue: row.TotalValue || 0,
        initialAbsoluteValue: row.InitialAbsoluteValue || 0,
        positionInContainer: row.L1PositionId ?? 0,
        physicalPositionId,
        partTypeId: actualPartTypeId!,
        lastTaskTimestamp: row.LastTaskTimestamp ?? null,
        lastTaskId: row.LastTaskId ?? null,
      });
    }
  }

  return shellInfoMap;
};

/**
 * Build a map of Shell IDs to their positions
 * Positions 1-12 belong to Upper Shell, positions 13-17 belong to Lower Shell
 */
const buildShellPositionsMap = (
  apiData: PartsTreeRowAPI[],
): Map<number, PartsTreeRowAPI[]> => {
  const shellPositionsMap = new Map<number, PartsTreeRowAPI[]>();

  // Filter position rows (children of shells)
  // These are rows where L1PartTypeId is a part type (301-310) that goes inside a shell
  // and L1PartId is a Shell (600 or 601)
  const positionRows = apiData.filter(
    (row) =>
      row.L1PartTypeId >= PART_TYPE_IDS.SIDEWALL_PANEL &&
      row.L1PartTypeId <= PART_TYPE_IDS.BURNER_NOZZLE &&
      row.L1PartId !== null &&
      row.L1PositionId !== null,
  );

  for (const row of positionRows) {
    const shellId = row.L1PartId;
    if (shellId === null) continue;

    const positions = shellPositionsMap.get(shellId);
    if (positions) {
      positions.push(row);
    } else {
      shellPositionsMap.set(shellId, [row]);
    }
  }

  return shellPositionsMap;
};

/**
 * Build a map of BurnerPanel PartId -> sub-component positions
 * Sub-components (Block, Front Face, Water Jacket, Nozzle) have L1PartId = BurnerPanel PartId
 */
const buildBurnerSubComponentsMap = (
  apiData: PartsTreeRowAPI[],
): Map<number, PartsTreeRowAPI[]> => {
  const map = new Map<number, PartsTreeRowAPI[]>();

  // Sub-component types are 307-310
  const subComponentTypes = [
    PART_TYPE_IDS.BURNER_BLOCK,
    PART_TYPE_IDS.BURNER_FRONT_FACE,
    PART_TYPE_IDS.BURNER_WATER_JACKET,
    PART_TYPE_IDS.BURNER_NOZZLE,
  ] as const;

  for (const row of apiData) {
    if (
      row.L1PartId !== null &&
      row.L1PartTypeId !== null &&
      (subComponentTypes as readonly number[]).includes(row.L1PartTypeId) &&
      row.L1PositionId !== null
    ) {
      // L1PartId is the BurnerPanel PartId (601, 602, 603)
      const burnerPanelId = row.L1PartId;
      const subComponents = map.get(burnerPanelId);
      if (subComponents) {
        subComponents.push(row);
      } else {
        map.set(burnerPanelId, [row]);
      }
    }
  }

  return map;
};

/**
 * Separate shells by online/offline status
 */
const separateShellsByStatus = (
  shellInfoMap: Map<number, ShellInfo>,
): { onlineShells: number[]; offlineShells: number[] } => {
  const onlineShells: number[] = [];
  const offlineShells: number[] = [];

  for (const [shellId, info] of shellInfoMap) {
    if (info.status === "Online") {
      onlineShells.push(shellId);
    } else {
      offlineShells.push(shellId);
    }
  }

  // Sort by position: Upper Shell (position 1) before Lower Shell (position 2)
  onlineShells.sort((a, b) => {
    const posA = shellInfoMap.get(a)?.positionInContainer ?? 0;
    const posB = shellInfoMap.get(b)?.positionInContainer ?? 0;
    return posA - posB;
  });

  return { onlineShells, offlineShells };
};

/**
 * Check if a PartTypeId is a BurnerPanel fixture (not a user-installed part)
 * BurnerPanels (PartTypeId 302) are permanent fixtures that hold sub-components
 */
const isBurnerPanelFixture = (partTypeId: number | null | undefined): boolean =>
  partTypeId === PART_TYPE_IDS.SIDEWALL_BURNER_PANEL;

/**
 * Create a burner sub-component row (Block, Front Face, Water Jacket, Nozzle)
 * These are children of a BurnerPanel fixture
 * @param apiPosition - Optional: actual API data if a part is installed in this position
 */
const createBurnerSubComponentRow = (
  shellId: number,
  shellInfo: ShellInfo,
  burnerPanelPartId: number, // The BurnerPanel PartId (604, 605, 606) - used as ParentPartId for installation
  parentPositionId: number, // The Upper Shell position (3, 5, 9)
  parentPositionPrefix: string, // Stripped L1PositionName, e.g. "Sidewall 3" (derived from API)
  childPosition: ExpectedChildPosition,
  parentPath: string[],
  apiPosition?: PartsTreeRowAPI, // Real data from API if part is installed
): SidewallTreeRow => {
  // Check if there's a part installed (from API data)
  const hasPart =
    apiPosition?.L2PartId !== null && apiPosition?.L2PartId !== undefined;

  const partInfo: PartInfo | null = hasPart
    ? {
        Make: apiPosition?.Make || "",
        Model: apiPosition?.Model || "",
        Vendor: apiPosition?.Vendor || "",
        SerialNumber: apiPosition?.SerialNumber || "",
      }
    : null;

  // Build descriptive position name including parent panel prefix
  // e.g., "Sidewall 3 Water Jacket" instead of just "Water Jacket"
  // parentPositionPrefix is derived from L1PositionName (e.g., "Sidewall 3" from "Sidewall 3 (#1 Burner)")
  const descriptivePositionName = `${parentPositionPrefix} ${childPosition.positionName}`;

  return {
    id: `location-${shellId}-${parentPositionId}-${childPosition.positionId}`,
    path: [...parentPath, childPosition.positionName],
    rowType: "location",

    EquipmentId: shellId,
    EquipmentName: shellInfo.name,
    Status: shellInfo.status,
    PositionId: childPosition.positionId, // Position within BurnerPanel (1-4)
    PositionName: descriptivePositionName,
    PartTypeId: childPosition.partTypeId,
    L2PartId: hasPart && apiPosition ? apiPosition.L2PartId : null,
    L2PartName: hasPart && apiPosition ? apiPosition.L2PartName : null,
    HasAssignedPart: hasPart,
    PartInfo: partInfo,
    PartStatus: hasPart ? "Installed" : "Empty",
    Heats: hasPart ? (apiPosition?.CampaignValue ?? null) : null,
    HeatsSinceRepair: hasPart ? (apiPosition?.PartialValue ?? null) : null,
    TotalHeats: apiPosition?.TotalValue || 0,
    InitialAbsoluteValue: apiPosition?.InitialAbsoluteValue || 0,
    StatusCode: apiPosition?.StatusCode ?? "B",
    InstalledDate: apiPosition?.LastTaskTimestamp ?? null,
    LastTaskId: apiPosition?.LastTaskId ?? null,
    Info: hasPart
      ? [apiPosition?.Make, apiPosition?.Model].filter(Boolean).join(" ") || "-"
      : null,
    Comments: apiPosition?.Comments ?? null,
    HasChildren: false,
    IsReplaceable: apiPosition?.IsReplaceable ?? undefined,
    IsArray: apiPosition?.IsArray ?? undefined,
    SortOrder: apiPosition?.SortOrder ?? undefined,
    // Store BurnerPanel PartId for API calls (MovePart needs ParentPartId = BurnerPanel)
    BurnerPanelPartId: burnerPanelPartId,
    ParentBurnerPositionId: parentPositionId,
  };
};

/**
 * Create a shell location row (Upper Shell or Lower Shell)
 * Shells are container positions that group other positions
 */
const createShellRow = (
  shellId: number,
  shellInfo: ShellInfo,
  position: PartsTreeRowAPI,
  basePath: string[],
): SidewallTreeRow => {
  const positionId = position.L1PositionId ?? 0;
  const hasPart = position.L2PartId !== null;

  const partInfo: PartInfo | null = hasPart
    ? {
        Make: position.Make || "",
        Model: position.Model || "",
        Vendor: position.Vendor || "",
        SerialNumber: position.SerialNumber || "",
      }
    : null;

  return {
    id: `shell-${shellId}-${positionId}`,
    path: [...basePath, position.L1PositionName ?? `Position ${positionId}`],
    rowType: "location",

    EquipmentId: shellId,
    EquipmentName: shellInfo.name,
    Status: shellInfo.status,
    PositionId: positionId,
    PositionName: position.L1PositionName ?? `Position ${positionId}`,
    PartTypeId: position.L1PartTypeId,
    L2PartId: hasPart ? position.L2PartId : null,
    L2PartName: hasPart ? position.L2PartName : null,
    HasAssignedPart: hasPart,
    PartInfo: partInfo,
    PartStatus: hasPart ? "Installed" : "Empty",
    Heats: hasPart ? (position.CampaignValue ?? null) : null,
    HeatsSinceRepair: hasPart ? (position.PartialValue ?? null) : null,
    TotalHeats: position.TotalValue || 0,
    InitialAbsoluteValue: position.InitialAbsoluteValue || 0,
    StatusCode: position.StatusCode,
    InstalledDate: position.LastTaskTimestamp ?? null,
    LastTaskId: position.LastTaskId ?? null,
    Info: hasPart
      ? [position.Make, position.Model].filter(Boolean).join(" ") || "-"
      : null,
    Comments: position.Comments,
    HasChildren: true, // Shells always have children (other positions)
    IsReplaceable: position.IsReplaceable ?? undefined,
    IsArray: position.IsArray ?? undefined,
    SortOrder: position.SortOrder ?? undefined,
  };
};

/**
 * Create a location row for a panel/component position
 */
const createLocationRow = (
  shellId: number,
  shellInfo: ShellInfo,
  position: PartsTreeRowAPI,
  shellPath: string[],
): SidewallTreeRow => {
  const positionId = position.L1PositionId ?? 0;
  const isBurnerPosition = (BURNER_POSITIONS as readonly number[]).includes(
    positionId,
  );

  // Check if the installed part is a BurnerPanel fixture
  // BurnerPanels are structural fixtures, NOT user-installed parts
  // We need to check L1PartTypeId which is the expected type for this position
  const installedPartIsBurnerFixture = isBurnerPanelFixture(
    position.L1PartTypeId,
  );

  // A position "has a part" only if it has an L2PartId AND it's NOT a BurnerPanel fixture
  // BurnerPanels are invisible fixtures - they don't count as "installed parts" for the user
  const hasPart = position.L2PartId !== null && !installedPartIsBurnerFixture;

  const partInfo: PartInfo | null = hasPart
    ? {
        Make: position.Make || "",
        Model: position.Model || "",
        Vendor: position.Vendor || "",
        SerialNumber: position.SerialNumber || "",
      }
    : null;

  return {
    id: `location-${shellId}-${positionId}`,
    path: [...shellPath, position.L1PositionName ?? `Position ${positionId}`],
    rowType: "location",

    EquipmentId: shellId,
    EquipmentName: shellInfo.name,
    Status: shellInfo.status,
    PositionId: positionId,
    PositionName: position.L1PositionName ?? `Position ${positionId}`,
    PartTypeId: position.L1PartTypeId,
    L2PartId: hasPart ? position.L2PartId : null, // Hide BurnerPanel PartId from UI
    L2PartName: hasPart ? position.L2PartName : null,
    HasAssignedPart: hasPart,
    PartInfo: partInfo,
    PartStatus: hasPart ? "Installed" : "Empty",
    Heats: hasPart ? (position.CampaignValue ?? null) : null,
    HeatsSinceRepair: hasPart ? (position.PartialValue ?? null) : null,
    TotalHeats: position.TotalValue || 0,
    InitialAbsoluteValue: position.InitialAbsoluteValue || 0,
    StatusCode: position.StatusCode,
    InstalledDate: position.LastTaskTimestamp ?? null,
    LastTaskId: position.LastTaskId ?? null,
    Info: hasPart
      ? [position.Make, position.Model].filter(Boolean).join(" ") || "-"
      : null,
    Comments: position.Comments,
    HasChildren: isBurnerPosition, // Burner positions always have children (sub-components)
    IsReplaceable: position.IsReplaceable ?? undefined,
    IsArray: position.IsArray ?? undefined,
    SortOrder: position.SortOrder ?? undefined,
    // Store BurnerPanel PartId if this is a burner position with fixture installed
    BurnerPanelPartId: installedPartIsBurnerFixture
      ? (position.L2PartId ?? undefined)
      : undefined,
  };
};

/**
 * Create a spare part row
 */
const createSparePartRow = (
  containerId: number,
  containerName: string,
  part: PartsTreeRowAPI,
  containerPath: string[],
): SidewallTreeRow => {
  const partInfo: PartInfo = {
    Make: part.Make || "",
    Model: part.Model || "",
    Vendor: part.Vendor || "",
    SerialNumber: part.SerialNumber || "",
  };

  const partDisplayName =
    [part.SerialNumber, part.Make, part.Model].filter(Boolean).join(" ") || "-";

  // Append PartId to make path unique
  const uniquePathName = `${partDisplayName} (#${part.L2PartId})`;

  return {
    id: `spare-${containerId}-${part.L2PartId}`,
    path: [...containerPath, uniquePathName],
    rowType: "sparePart",

    ContainerId: containerId,
    ContainerName: containerName,
    EquipmentId: containerId,
    L2PartId: part.L2PartId!,
    L2PartName: part.L2PartName,
    PartInfo: partInfo,
    PartTypeId: part.L1PartTypeId,
    TypeIdentity: part.IAm
      ? part.IAm.split(",").map(Number)
      : [part.L1PartTypeId],
    Heats: part.CampaignValue ?? null,
    HeatsSinceRepair: part.PartialValue ?? null,
    TotalHeats: part.TotalValue || 0,
    InitialAbsoluteValue: part.InitialAbsoluteValue || 0,
    StatusCode: part.StatusCode,
    InstalledDate: part.LastTaskTimestamp ?? null,
    LastTaskId: part.LastTaskId ?? null,
    HasAssignedPart: true,
    IsReplaceable: part.IsReplaceable ?? undefined,
    IsArray: part.IsArray ?? undefined,
    SortOrder: part.SortOrder ?? undefined,
  };
};

/**
 * Add sub-components for a burner position (Block, Front Face, Water Jacket, Nozzle)
 */
const addBurnerSubComponents = (
  result: SidewallTreeRow[],
  shellId: number,
  shellInfo: ShellInfo,
  burnerPosition: PartsTreeRowAPI,
  shellPath: string[],
  burnerSubComponentsMap: Map<number, PartsTreeRowAPI[]>,
): void => {
  const childPositions = parseExpectedChildPositions(
    burnerPosition.ExpectedChildPositionIds,
    burnerPosition.ExpectedChildPositionNames,
    burnerPosition.ExpectedChildPartTypeIds,
    burnerPosition.ExpectedChildPartTypeNames,
  );

  if (childPositions.length === 0) return;

  const positionId = burnerPosition.L1PositionId ?? 0;
  const burnerPath = [
    ...shellPath,
    burnerPosition.L1PositionName ?? `Position ${positionId}`,
  ];

  const burnerPanelId = burnerPosition.L2PartId!;
  const actualSubComponents = burnerSubComponentsMap.get(burnerPanelId) || [];

  // Derive parent prefix from L1PositionName (e.g., "Sidewall 3 (#1 Burner)" → "Sidewall 3")
  const parentPrefix = stripBurnerAnnotation(
    burnerPosition.L1PositionName,
    positionId,
  );

  for (const childPos of childPositions) {
    const apiPosition = actualSubComponents.find(
      (sc) => sc.L1PositionId === childPos.positionId,
    );

    result.push(
      createBurnerSubComponentRow(
        shellId,
        shellInfo,
        burnerPanelId,
        positionId,
        parentPrefix,
        childPos,
        burnerPath,
        apiPosition,
      ),
    );
  }
};

/**
 * Add a shell and its positions to the result array
 * Hierarchy: Container > Shell (Upper/Lower) > Positions
 *
 * With the new DB structure:
 * - Upper Shell (600) and Lower Shell (601) are Parts under containers (500/501)
 * - Each shell contains positions: Upper has 1-12, Lower has 13-17
 * - Burner positions (3, 5, 9) in Upper Shell have sub-components
 */
const addShellWithPositions = (
  result: SidewallTreeRow[],
  shellId: number,
  shellInfo: ShellInfo,
  basePath: string[],
  shellPositionsMap: Map<number, PartsTreeRowAPI[]>,
  burnerSubComponentsMap: Map<number, PartsTreeRowAPI[]>,
): void => {
  // Shell path: Container > Shell Name
  const shellPath = [...basePath, shellInfo.name];

  // Create an equipment-like row for the shell itself (with HasChildren: true)
  // Shells behave like equipment: have metrics, can go online/offline, have children
  result.push({
    id: `equipment-${shellId}`,
    path: shellPath,
    rowType: "location",

    EquipmentId: shellId,
    EquipmentName: shellInfo.name,
    Status: shellInfo.status,
    PositionId: shellInfo.physicalPositionId, // Physical position (18=Lower, 19=Upper) for SVG/3D
    PositionName: shellInfo.name,
    PartTypeId: shellInfo.partTypeId, // 306=Upper-Shell, 311=Lower-Shell
    L2PartId: shellId, // The shell Part itself
    L2PartName: shellInfo.name,
    HasAssignedPart: true, // Shell is always "installed"
    PartInfo: null, // Shells don't have Make/Model/Vendor
    PartStatus: "Installed",
    Heats: shellInfo.campaignValue,
    HeatsSinceRepair: 0,
    TotalHeats: shellInfo.totalValue,
    InitialAbsoluteValue: shellInfo.initialAbsoluteValue,
    StatusCode: "O", // Shells are always OK status
    InstalledDate: shellInfo.lastTaskTimestamp,
    LastTaskId: shellInfo.lastTaskId,
    Info: null,
    Comments: null,
    HasChildren: true, // Shells always have child positions
    IsReplaceable: false, // Shells are structural containers
    IsArray: false,
  });

  // Get all positions for this shell
  const positions = shellPositionsMap.get(shellId) || [];

  // Sort positions by PositionId, deduplicate
  const sortedPositions = sortAndDeduplicatePositions(positions);

  // Add each position under the shell
  for (const pos of sortedPositions) {
    const positionId = pos.L1PositionId ?? 0;

    const locationRow = createLocationRow(shellId, shellInfo, pos, shellPath);
    result.push(locationRow);

    // For burner positions (3, 5, 9) with a BurnerPanel fixture installed,
    // generate the sub-component rows (Block, Front Face, Water Jacket, Nozzle)
    const isBurnerPosition = (BURNER_POSITIONS as readonly number[]).includes(
      positionId,
    );
    const hasBurnerFixture =
      isBurnerPanelFixture(pos.L1PartTypeId) && pos.L2PartId !== null;

    if (isBurnerPosition && hasBurnerFixture) {
      addBurnerSubComponents(
        result,
        shellId,
        shellInfo,
        pos,
        shellPath,
        burnerSubComponentsMap,
      );
    }
  }
};

/**
 * Add spares containers and their spare parts to the result array
 */
const addSparesContainers = (
  result: SidewallTreeRow[],
  sidewallPartsSparesPath: string[],
  sparePartsMap: Map<number, PartsTreeRowAPI[]>,
): void => {
  for (const containerId of SPARES_CONTAINER_ORDER) {
    const containerName = SPARES_CONTAINER_NAMES[containerId];
    if (!containerName) continue; // Skip if mapping not found
    const containerPath = [...sidewallPartsSparesPath, containerName];

    // Add the spares container row
    result.push(
      createContainerRow<SidewallTreeRow>(
        containerId,
        containerName,
        containerPath,
        "sparesContainer",
      ),
    );

    // Add spare parts in this container
    const spareParts = sparePartsMap.get(containerId) || [];
    for (const part of spareParts) {
      if (!part.L2PartId) continue;
      result.push(
        createSparePartRow(containerId, containerName, part, containerPath),
      );
    }
  }
};

// ============================================
// Main Transform Function
// ============================================

/**
 * Transform API response from GetPartsTree (online + offline) to frontend SidewallTreeRow[]
 *
 * Creates full hierarchy:
 * - Sidewall-Online
 *   - Sidewall #X (online)
 *     - panels...
 * - Sidewall-Offline
 *   - Sidewall Spares
 *     - Sidewall #Y (offline)
 *       - panels...
 *   - Sidewall Parts Spares
 *     - Panel Spares
 *       - spare parts...
 *
 * @param apiData - Raw rows from EQP.GetPartsTree (online + offline)
 * @returns Flattened tree data for ag-Grid
 */
export const transformAPIToTreeData = (
  apiData: PartsTreeRowAPI[],
): SidewallTreeRow[] => {
  const result: SidewallTreeRow[] = [];

  // Step 1: Build lookup maps
  // Shells (Upper Shell 600, Lower Shell 601) are direct children of containers
  const shellInfoMap = buildShellInfoMap(apiData);
  const shellPositionsMap = buildShellPositionsMap(apiData);
  const burnerSubComponentsMap = buildBurnerSubComponentsMap(apiData);
  const sparesContainerIds = Object.keys(SPARES_CONTAINER_NAMES).map(Number);
  const sparePartsMap = buildSparePartsMap(apiData, sparesContainerIds);

  // Step 2: Separate shells by online/offline status
  const { onlineShells, offlineShells } = separateShellsByStatus(shellInfoMap);

  // ==========================================
  // BUILD THE HIERARCHY
  // ==========================================

  // 1. Sidewall-Online container
  const eafOnlinePath = [CONTAINER_NAMES.ONLINE];
  result.push(
    createContainerRow<SidewallTreeRow>(
      PART_IDS.SIDEWALL,
      CONTAINER_NAMES.ONLINE,
      eafOnlinePath,
      "container",
    ),
  );

  // Add slot containers (Upper Location, Lower Location) under Online
  // Then add shells under their respective slots
  // Slots are identified from the API data as rows with L1PartTypeId = 312/313
  const slotRows = apiData.filter(
    (r) => isShellSlotType(r.L1PartTypeId) && r.L2PartId,
  );
  for (const slotRow of slotRows) {
    const slotId = slotRow.L2PartId!;
    const slotName = slotRow.L2PartName || `Slot ${slotId}`;
    const slotPath = [...eafOnlinePath, slotName];

    // Check if a shell is installed on this slot
    const hasShellInstalled = onlineShells.some((shellId) => {
      const shellInfo = shellInfoMap.get(shellId);
      return shellInfo?.parentId === slotId;
    });

    // Add slot as a container row (like Strand in Caster)
    const slotContainer = createContainerRow<SidewallTreeRow>(
      slotId,
      slotName,
      slotPath,
      "container",
    );
    slotContainer.HasShellInstalled = hasShellInstalled;
    slotContainer.SlotPartTypeId = slotRow.L1PartTypeId ?? undefined;
    // InstalledDate on slot = slot's last task timestamp (used as location constraint
    // when installing a shell — e.g., when the previous shell was uninstalled)
    slotContainer.InstalledDate = slotRow.LastTaskTimestamp ?? null;
    result.push(slotContainer);

    // Find the shell installed on this slot
    for (const shellId of onlineShells) {
      const shellInfo = shellInfoMap.get(shellId);
      if (shellInfo && shellInfo.parentId === slotId) {
        addShellWithPositions(
          result,
          shellId,
          shellInfo,
          slotPath,
          shellPositionsMap,
          burnerSubComponentsMap,
        );
      }
    }
  }

  // Fallback: if no slots found (old structure), add shells directly under Online
  if (slotRows.length === 0) {
    for (const shellId of onlineShells) {
      const shellInfo = shellInfoMap.get(shellId);
      if (shellInfo) {
        addShellWithPositions(
          result,
          shellId,
          shellInfo,
          eafOnlinePath,
          shellPositionsMap,
          burnerSubComponentsMap,
        );
      }
    }
  }

  // 2. Sidewall-Offline container
  const eafOfflinePath = [CONTAINER_NAMES.SPARES];
  result.push(
    createContainerRow<SidewallTreeRow>(
      PART_IDS.SPARES,
      CONTAINER_NAMES.SPARES,
      eafOfflinePath,
      "container",
    ),
  );

  // 2a. Shell Spares container (contains offline shells)
  const shellSparesPath = [...eafOfflinePath, CONTAINER_NAMES.SHELL_SPARES];
  result.push(
    createContainerRow<SidewallTreeRow>(
      PART_IDS.SHELL_SPARES,
      CONTAINER_NAMES.SHELL_SPARES,
      shellSparesPath,
      "sparesContainer",
    ),
  );

  // Add offline shells under Shell Spares
  for (const shellId of offlineShells) {
    const shellInfo = shellInfoMap.get(shellId);
    if (shellInfo) {
      addShellWithPositions(
        result,
        shellId,
        shellInfo,
        shellSparesPath,
        shellPositionsMap,
        burnerSubComponentsMap,
      );
    }
  }

  // 2b. Shell Parts Spares container (contains panel spares by type)
  const shellPartsSparesPath = [
    ...eafOfflinePath,
    CONTAINER_NAMES.SHELL_PARTS_SPARES,
  ];
  result.push(
    createContainerRow<SidewallTreeRow>(
      PART_IDS.SHELL_PARTS_SPARES,
      CONTAINER_NAMES.SHELL_PARTS_SPARES,
      shellPartsSparesPath,
      "sparesContainer",
    ),
  );

  // Add spares containers under Shell Parts Spares
  addSparesContainers(result, shellPartsSparesPath, sparePartsMap);

  return result;
};
